import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MobileShell } from "@/components/MobileShell";
import { AddressForm } from "@/components/account/AddressForm";
import { useAuthSession } from "@/context/AuthSessionContext";
import { Button } from "@/components/ui/button";
import { PencilLine, Trash2, Star } from "lucide-react";

function addressToFormValues(address, fallbackIsDefault = false) {
    if (!address) {
        return { isDefault: fallbackIsDefault };
    }

    return {
        firstName: address.first_name || "",
        lastName: address.last_name || "",
        addressLine1: address.address_line1 || "",
        addressLine2: address.address_line2 || "",
        city: address.city || "",
        pinCode: address.pin_code || "",
        phoneNumber: address.phone_number || "",
        countryCode: address.country_code || "",
        stateCode: address.state_code || "",
        isDefault: Boolean(address.is_default),
    };
}

function addressPayloadFromForm(formPayload, fallbackIsDefault = false) {
    const addressLine2Raw = formPayload.address_line2 ?? formPayload.addressLine2 ?? "";
    const addressLine2 = typeof addressLine2Raw === "string" ? addressLine2Raw.trim() : "";

    return {
        country_code: formPayload.country_code || formPayload.countryCode,
        state_code: formPayload.state_code || formPayload.stateCode,
        first_name: formPayload.first_name || formPayload.firstName,
        last_name: formPayload.last_name || formPayload.lastName,
        company: null,
        address_line1: formPayload.address_line1 || formPayload.addressLine1,
        address_line2: addressLine2 || null,
        city: formPayload.city,
        pin_code: formPayload.pin_code || formPayload.pinCode,
        phone_number: formPayload.phone_number || formPayload.phoneNumber,
        is_default: Boolean(fallbackIsDefault || formPayload.is_default),
    };
}

export default function Account() {
    const {
        user,
        isAuthenticated,
        clearAuthSession,
        addresses,
        addressesLoading,
        createAddress,
        updateAddress,
        deleteAddress,
        addressStructure,
        addressStructureLoading,
    } = useAuthSession();

    const [saveAddressPending, setSaveAddressPending] = useState(false);
    const [addressFormOpen, setAddressFormOpen] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);
    const [deletingAddressId, setDeletingAddressId] = useState("");

    useEffect(() => {
        if (!addressesLoading && addresses.length === 0) {
            setAddressFormOpen(true);
        }
    }, [addressesLoading, addresses.length]);

    const countries = useMemo(
        () => (Array.isArray(addressStructure?.countries) ? addressStructure.countries : []),
        [addressStructure]
    );

    const countryByCode = useMemo(
        () => new Map(countries.map((country) => [country.country_code, country])),
        [countries]
    );

    const saveAddress = async (payload) => {
        setSaveAddressPending(true);
        try {
            if (editingAddress) {
                await updateAddress(editingAddress.address_id, payload);
                toast.success("Address updated successfully.");
            } else {
                await createAddress(payload);
                toast.success("Address saved successfully.");
            }
            setEditingAddress(null);
            setAddressFormOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || (editingAddress ? "Could not update address" : "Could not save address"));
            throw e;
        } finally {
            setSaveAddressPending(false);
        }
    };

    const startAddAddress = () => {
        setEditingAddress(null);
        setAddressFormOpen(true);
    };

    const startEditAddress = (address) => {
        setEditingAddress(address);
        setAddressFormOpen(true);
    };

    const cancelAddressForm = () => {
        setEditingAddress(null);
        setAddressFormOpen(false);
    };

    const makeDefault = async (address) => {
        if (address.is_default) return;

        setSaveAddressPending(true);
        try {
            await updateAddress(address.address_id, addressPayloadFromForm(addressToFormValues(address), true));
            toast.success("Default address updated.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update default address");
        } finally {
            setSaveAddressPending(false);
        }
    };

    const removeAddress = async (address) => {
        const confirmed = window.confirm("Delete this address?");
        if (!confirmed) return;

        setDeletingAddressId(address.address_id);
        try {
            await deleteAddress(address.address_id);
            toast.success("Address deleted.");
            if (editingAddress?.address_id === address.address_id) {
                cancelAddressForm();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not delete address");
        } finally {
            setDeletingAddressId("");
        }
    };

    const formInitialValues = useMemo(
        () => addressToFormValues(editingAddress, addresses.length === 0),
        [editingAddress, addresses.length]
    );

    const isAddressFormVisible = addressFormOpen || Boolean(editingAddress) || addresses.length === 0;

    return (
        <MobileShell showCart={false}>
            <div className="pt-4 md:pt-8 pb-12 space-y-6" data-testid="account-page">
                <header className="space-y-2">
                    <h1 className="font-serif text-4xl md:text-5xl">My Account</h1>
                    <p className="text-sm text-muted-foreground">
                        Manage your profile and save delivery addresses.
                    </p>
                </header>

                {!isAuthenticated ? (
                    <div className="rounded-3xl border border-border/60 bg-surface/30 p-5 space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Sign in to view your saved addresses and manage delivery details.
                        </p>
                        <Link
                            to="/account/email"
                            className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-5 py-3 text-sm hover:bg-primary-hover transition-colors"
                        >
                            Login with email
                        </Link>
                    </div>
                ) : (
                    <section className="space-y-6">
                        <div className="rounded-3xl border border-border/60 bg-surface/30 p-5 flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Logged in as</p>
                                <p className="text-base text-foreground">{user?.email || "Verified user"}</p>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                className="rounded-full"
                                onClick={clearAuthSession}
                                data-testid="logout-button"
                            >
                                Logout
                            </Button>
                        </div>

                        <div className="rounded-3xl border border-border/60 bg-surface/30 p-5 space-y-4">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <h2 className="font-serif text-2xl">{editingAddress ? "Edit address" : "Add a new address"}</h2>
                                    <p className="text-sm text-muted-foreground">
                                        {editingAddress ? "Update the selected address and choose whether it is the default." : "Add a delivery address without leaving the account page."}
                                    </p>
                                </div>
                                {isAddressFormVisible ? (
                                    editingAddress || addresses.length > 0 ? (
                                        <Button type="button" variant="outline" className="rounded-full" onClick={cancelAddressForm} disabled={saveAddressPending}>
                                            Close
                                        </Button>
                                    ) : null
                                ) : (
                                    <Button type="button" className="rounded-full" onClick={startAddAddress}>
                                        Add a new address
                                    </Button>
                                )}
                            </div>

                            {isAddressFormVisible ? (
                                addressStructureLoading ? (
                                    <p className="text-sm text-muted-foreground">Loading country and state lists...</p>
                                ) : (
                                    <AddressForm
                                        countries={countries}
                                        initialValues={formInitialValues}
                                        onSubmit={saveAddress}
                                        onCancel={addresses.length > 0 ? cancelAddressForm : undefined}
                                        isSubmitting={saveAddressPending}
                                        submitLabel={editingAddress ? "Update address" : "Save address"}
                                        hideCountry
                                        showDefaultOption
                                    />
                                )
                            ) : (
                                <p className="text-sm text-muted-foreground">Click Add a new address to enter delivery details.</p>
                            )}
                        </div>

                        <div className="rounded-3xl border border-border/60 bg-surface/30 p-5 space-y-4">
                            <h2 className="font-serif text-2xl">Saved addresses</h2>

                            {addressesLoading ? (
                                <p className="text-sm text-muted-foreground">Loading addresses...</p>
                            ) : addresses.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No saved addresses yet. Add one above.</p>
                            ) : (
                                <div className="space-y-3">
                                    {addresses.map((address) => {
                                        const country = countryByCode.get(address.country_code);
                                        const state = country?.states?.find((s) => s.state_code === address.state_code);
                                        return (
                                            <article
                                                key={address.address_id}
                                                className="rounded-2xl border border-border/60 bg-background/80 p-4 space-y-3"
                                            >
                                                <div className="flex flex-wrap items-start justify-between gap-3">
                                                    <div>
                                                        <p className="font-medium text-foreground inline-flex items-center gap-2">
                                                            {address.first_name} {address.last_name}
                                                            {address.is_default && (
                                                                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-primary">
                                                                    <Star className="h-3 w-3 fill-current" /> Default
                                                                </span>
                                                            )}
                                                        </p>
                                                        <p className="text-sm text-muted-foreground mt-1">
                                                            {[address.address_line1, address.address_line2].filter(Boolean).join(", ")}, {address.city}, {state?.state_name || address.state_code}, {country?.country_name || address.country_code} - {address.pin_code}
                                                        </p>
                                                        <p className="text-sm text-muted-foreground mt-1">Phone: {address.phone_number}</p>
                                                    </div>

                                                    <div className="flex flex-wrap gap-2">
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            className="rounded-full"
                                                            onClick={() => startEditAddress(address)}
                                                            disabled={saveAddressPending || deletingAddressId === address.address_id}
                                                        >
                                                            <PencilLine className="h-4 w-4 mr-2" /> Edit
                                                        </Button>
                                                        {!address.is_default && (
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                className="rounded-full"
                                                                onClick={() => makeDefault(address)}
                                                                disabled={saveAddressPending || deletingAddressId === address.address_id}
                                                            >
                                                                <Star className="h-4 w-4 mr-2" /> Make default
                                                            </Button>
                                                        )}
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            className="rounded-full text-destructive hover:text-destructive"
                                                            onClick={() => removeAddress(address)}
                                                            disabled={saveAddressPending || deletingAddressId === address.address_id}
                                                        >
                                                            <Trash2 className="h-4 w-4 mr-2" />
                                                            {deletingAddressId === address.address_id ? "Deleting..." : "Delete"}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </section>
                )}
            </div>
        </MobileShell>
    );
}
